/**
 * Custom engraving validation.
 *
 * Only Line 1 of the "Personalize Your Product" field is required. When empty,
 * blocks Add to cart / Buy Now, shows an inline field error and a modal
 * (legacy behavior), and focuses the field. Typing clears the inline error.
 *
 * Public API (window):
 * - validateCustomEngraving(root?) -> boolean (true = ok to proceed)
 * - clearEngravingFieldError(container)
 * - closeEngravingModal(modal)
 */
(function () {
  const CONTAINER_SELECTOR = '[data-custom-engraving]';
  const REQUIRED_INPUT_SELECTOR = '[data-engraving-line="1"]';
  const MODAL_SELECTOR = '[data-engraving-modal]';
  const DEFAULT_MESSAGE = 'Please enter your custom requirements.';
  const FOCUSABLE_SELECTOR =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  let lastFocusedElement = null;
  let activeModal = null;

  function getScope(root) {
    if (root && typeof root.querySelector === 'function') return root;
    return document;
  }

  /** All engraving containers within a scope (PDP may host one). */
  function getContainers(root) {
    const scope = getScope(root);
    const containers = [];
    if (scope.matches && scope.matches(CONTAINER_SELECTOR)) containers.push(scope);
    scope.querySelectorAll(CONTAINER_SELECTOR).forEach((el) => {
      if (!containers.includes(el)) containers.push(el);
    });
    return containers;
  }

  function getRequiredInput(container) {
    return container.querySelector(REQUIRED_INPUT_SELECTOR);
  }

  function associateModal(container, modal) {
    if (!modal.id) {
      modal.id = `engraving-modal-${Math.random().toString(36).slice(2, 11)}`;
    }
    container.setAttribute('data-engraving-modal-id', modal.id);
    return modal;
  }

  function getModal(container) {
    const associatedId = container.getAttribute('data-engraving-modal-id');
    if (associatedId) {
      const byId = document.getElementById(associatedId);
      if (byId) return byId;
    }

    // Modal is a sibling rendered right after the container in the snippet.
    // Associate before any reparent-to-body so later lookups stay product-scoped.
    let el = container.nextElementSibling;
    while (el) {
      if (el.matches && el.matches(MODAL_SELECTOR)) {
        return associateModal(container, el);
      }
      el = el.nextElementSibling;
    }
    return null;
  }

  function showFieldError(container, input) {
    input.classList.add('is-error');
    input.setAttribute('aria-invalid', 'true');
    const errorEl = container.querySelector('[data-engraving-error]');
    if (errorEl) errorEl.hidden = false;
  }

  function clearEngravingFieldError(container) {
    if (!container) return;
    const input = getRequiredInput(container);
    if (input) {
      input.classList.remove('is-error');
      input.removeAttribute('aria-invalid');
    }
    const errorEl = container.querySelector('[data-engraving-error]');
    if (errorEl) errorEl.hidden = true;
  }

  function getModalFocusables(modal) {
    const box = modal.querySelector('.engraving-required-modal__box');
    const items = Array.from(modal.querySelectorAll(FOCUSABLE_SELECTOR));
    // Keep the dialog box in the cycle so initial focus (tabindex=-1) is trapped.
    if (box && !items.includes(box)) items.unshift(box);
    return items;
  }

  function closeEngravingModal(modal) {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    if (activeModal === modal) activeModal = null;
    document.removeEventListener('keydown', onKeydown);
    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
    lastFocusedElement = null;
  }

  function onKeydown(event) {
    const modal = activeModal;
    if (!modal || modal.hidden) return;

    if (event.key === 'Escape') {
      closeEngravingModal(modal);
      return;
    }

    if (event.key !== 'Tab') return;

    const focusables = getModalFocusables(modal);
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !modal.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !modal.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  }

  function openModal(container, input) {
    const modal = getModal(container);
    if (!modal) return;
    const message =
      container.getAttribute('data-engraving-error-message') || DEFAULT_MESSAGE;
    const messageEl = modal.querySelector('.engraving-required-modal__message');
    if (messageEl) messageEl.textContent = message;

    // Reparent to <body> so position:fixed is relative to the viewport,
    // not to any transformed/contained PDP ancestor (which would offset it).
    // Association (data-engraving-modal-id) is already set in getModal.
    if (modal.parentNode !== document.body) {
      document.body.appendChild(modal);
    }

    lastFocusedElement = input || document.activeElement;
    activeModal = modal;
    modal.hidden = false;
    document.addEventListener('keydown', onKeydown);

    const box = modal.querySelector('.engraving-required-modal__box');
    if (box && typeof box.focus === 'function') box.focus();
  }

  /**
   * @param {Element|Document} [root]
   * @returns {boolean} true if all required engraving fields are filled
   */
  function validateCustomEngraving(root) {
    const containers = getContainers(root);
    if (containers.length === 0) return true;

    let firstInvalidInput = null;
    let firstInvalidContainer = null;

    containers.forEach((container) => {
      const input = getRequiredInput(container);
      if (!input) return;
      const value = (input.value || '').trim();
      if (value === '') {
        showFieldError(container, input);
        if (!firstInvalidInput) {
          firstInvalidInput = input;
          firstInvalidContainer = container;
        }
      } else {
        clearEngravingFieldError(container);
      }
    });

    if (firstInvalidInput) {
      openModal(firstInvalidContainer, firstInvalidInput);
      return false;
    }
    return true;
  }

  function bindContainer(container) {
    if (container.dataset.engravingBound === 'true') return;
    container.dataset.engravingBound = 'true';

    const input = getRequiredInput(container);
    if (input) {
      input.addEventListener('input', () => clearEngravingFieldError(container));
    }

    const modal = getModal(container);
    if (modal && modal.dataset.engravingBound !== 'true') {
      modal.dataset.engravingBound = 'true';
      modal.querySelectorAll('[data-engraving-modal-close]').forEach((el) => {
        el.addEventListener('click', () => closeEngravingModal(modal));
      });
    }
  }

  function init(root) {
    getContainers(root).forEach(bindContainer);
  }

  window.validateCustomEngraving = validateCustomEngraving;
  window.clearEngravingFieldError = clearEngravingFieldError;
  window.closeEngravingModal = closeEngravingModal;
  window.initCustomEngravingValidation = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
})();
